import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SimulationWatchModal = ({ isOpen, onClose }) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef(null);
  const [envStats, setEnvStats] = useState({
    magicDensity: 87.2,
    realityStability: 98.5,
    timeflowRate: 1.0,
    dimensionalIntegrity: 92.8,
    populationCount: 145928,
    environmentType: "HIGH_FANTASY",
    threatLevel: "LOW",
    anomalyCount: 3,
  });

  useEffect(() => {
    if (!isOpen) {
      setIsPlaying(false);
      return;
    }
    setIsPlaying(true);
  }, [isOpen]);

  useEffect(() => {
    // Simulate fluctuating environment variables
    const interval = setInterval(() => {
      setEnvStats(prev => ({
        ...prev,
        magicDensity: Math.max(0, Math.min(100, prev.magicDensity + (Math.random() - 0.5) * 2)),
        realityStability: Math.max(0, Math.min(100, prev.realityStability + (Math.random() - 0.5))),
        dimensionalIntegrity: Math.max(0, Math.min(100, prev.dimensionalIntegrity + (Math.random() - 0.5))),
        anomalyCount: Math.max(0, prev.anomalyCount + (Math.random() > 0.8 ? 1 : Math.random() > 0.8 ? -1 : 0)),
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      setDuration(videoRef.current.duration);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      <motion.div 
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        className="relative bg-purple-950/90 border-2 border-cyan-400 p-8 rounded-lg w-full max-w-6xl mx-4"
      >
        <div className="absolute inset-0 overflow-hidden rounded-lg">
          <div className="simulation-scan-effect"></div>
        </div>

        <div className="relative z-10">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-cyan-400 text-2xl font-['VT323'] glitch-text">
              SIMULATION_WATCH.exe [FEY_CITY_07]
            </h2>
            <div className="text-[#39ff14] font-mono">
              TIMEFLOW: {envStats.timeflowRate}x
            </div>
          </div>

          <div className="grid grid-cols-[1fr_300px] gap-6">
            {/* Main Video Display */}
            <div className="relative">
              <div className="relative aspect-video bg-black rounded-lg overflow-hidden border border-cyan-400">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  src="/assets/images/l33t/feycity.mp4"
                  autoPlay={isPlaying}
                  loop
                  muted
                  onTimeUpdate={handleTimeUpdate}
                />
                <div className="video-interference"></div>
                <div className="absolute top-4 left-4 text-[#39ff14] font-mono text-sm">
                  REC: {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>
              
              {/* Video Controls */}
              <div className="mt-4 flex items-center space-x-4">
                <button
                  onClick={handlePlayPause}
                  className="crt-button bg-transparent text-cyan-400 border border-cyan-400 px-4 py-2 hover:bg-cyan-400/10"
                >
                  {isPlaying ? 'PAUSE' : 'PLAY'}
                </button>
                <button
                  onClick={() => videoRef.current && (videoRef.current.currentTime = 0)}
                  className="crt-button bg-transparent text-cyan-400 border border-cyan-400 px-4 py-2 hover:bg-cyan-400/10"
                >
                  RESET
                </button>
              </div>
            </div>

            {/* Environment Stats */}
            <div className="space-y-4 font-mono">
              <div className="bg-purple-950/50 p-4 rounded border border-[#39ff14]">
                <h3 className="text-[#39ff14] mb-2">&gt; ENVIRONMENT_STATS</h3>
                <div className="space-y-2">
                  <div>
                    <div className="text-cyan-400 text-sm">MAGIC_DENSITY</div>
                    <div className="bg-purple-900 rounded-full h-2">
                      <div 
                        className="bg-[#39ff14] h-full rounded-full transition-all duration-300"
                        style={{ width: `${envStats.magicDensity}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-cyan-400 text-sm">REALITY_STABILITY</div>
                    <div className="bg-purple-900 rounded-full h-2">
                      <div 
                        className="bg-cyan-400 h-full rounded-full transition-all duration-300"
                        style={{ width: `${envStats.realityStability}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-cyan-400 text-sm">DIMENSIONAL_INTEGRITY</div>
                    <div className="bg-purple-900 rounded-full h-2">
                      <div 
                        className="bg-fuchsia-400 h-full rounded-full transition-all duration-300"
                        style={{ width: `${envStats.dimensionalIntegrity}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-purple-950/50 p-4 rounded border border-cyan-400">
                <h3 className="text-cyan-400 mb-2">&gt; METRICS</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">ENV_TYPE:</span>
                    <span className="text-[#39ff14]">{envStats.environmentType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">POPULATION:</span>
                    <span className="text-[#39ff14]">{envStats.populationCount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">THREAT_LEVEL:</span>
                    <span className="text-[#39ff14]">{envStats.threatLevel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">ANOMALIES:</span>
                    <span className="text-[#39ff14]">{envStats.anomalyCount}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={onClose}
                className="w-full crt-button bg-transparent text-[#39ff14] border border-[#39ff14] p-2 hover:bg-[#39ff14]/10"
              >
                EXIT_OBSERVATION
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SimulationWatchModal;
